import {
    ComparatorReturns,
    FilterDirective,
    GroupingDirective,
    ListStore,
    SortingDirective,
} from '@list-store';
import type {
    ColumnDefinitionEnumType,
    QueueDisplayDensityEnumType,
    QueueColumnFilter,
    WorkspacePanelDto,
} from '@mark43/cad-api';
import { Option } from '../../forms/option';
import { action, makeObservable, observable, computed } from 'mobx';
import { EventNotificationsStore } from '../../event-notifications/event-notifications-store';
import columnDefinitionEnum from '../column-definition.enum';
import { isNotNullOrUndefined } from '../utils/is-not-null-or-undefined';
import { RawQueueRowData } from './types-2';
import { getEventQueueRowData } from '../utils/get-event-queue-row-data';
import type { WorkspacePanelWithQueue } from '../../panels/workspace-queue-exists';
import { WorkspaceQueue } from '../../panels/workspace-queue';
import { EVENT_UPDATE_NOTIFICATION_MODE } from '../../panels/constants';
import { ColumnDefinitionEnum } from '@mark43/cad-api';
import type { WithColumnDefinition } from '../../event-queues/get-event-filter-options';
import {
    alphabeticalStringComparator,
    alphanumericStringComparator,
    numberComparator,
} from '../../common/sorting/comparators';
import { logWarn } from '../../monitoring/log';

export type AedAlertRowContext = {
    agencyTypeGlobalAttrId: number | undefined;
};

export type NewEventAlertRowContext = {
    isAed: boolean;
    callTakerId: number | undefined;
    priorityId: number | null;
    agencyTypeGlobalAttrId: number | undefined;
};

/**
 * Sentinel value to signify empty groups.
 * This value will need to be replaced when rendering queue headers
 * as it should be language agnostic.
 */
export const EMPTY_GROUP_SENTINEL_HEADER = '__empty__';

export class QueueStore<T extends RawQueueRowData = RawQueueRowData> {
    public readonly listStore: ListStore<T>;

    @observable
    public workspacePanel: WorkspacePanelWithQueue;

    @observable
    public filterQuery: string = '';

    private eventNotificationsStore?: EventNotificationsStore;

    @observable
    public isLoading: boolean = true;

    @observable
    public isError: boolean = false;

    private playAedSoundForEvent?: (eventId: number, rowContext: AedAlertRowContext) => void;

    private playNewEventAlertSound?: (rowId: number, rowContext: NewEventAlertRowContext) => void;

    private onRemoveRows?: (eventId: number) => void;

    private manualFilterDirective: boolean = false;

    constructor({
        workspacePanel,
        eventNotificationsStore,
        playAedSoundForEvent,
        playNewEventAlertSound,
        onRemoveRows,
        defaultSortingDirective,
        manualFilterDirective,
        sortingDirective,
        groupingDirective,
    }: {
        workspacePanel: WorkspacePanelWithQueue;
        eventNotificationsStore?: EventNotificationsStore;
        playAedSoundForEvent?: (eventId: number, rowContext: AedAlertRowContext) => void;
        playNewEventAlertSound?: (rowId: number, rowContext: NewEventAlertRowContext) => void;
        onRemoveRows?: (eventId: number) => void;
        defaultSortingDirective?: SortingDirective<T>;
        manualFilterDirective?: boolean;
        sortingDirective?: SortingDirective<T>;
        groupingDirective?: GroupingDirective<T>;
    }) {
        this.workspacePanel = workspacePanel;
        this.eventNotificationsStore = eventNotificationsStore;
        this.playAedSoundForEvent = playAedSoundForEvent;
        this.playNewEventAlertSound = playNewEventAlertSound;
        this.onRemoveRows = onRemoveRows;
        this.manualFilterDirective = !!manualFilterDirective;
        this.listStore = new ListStore<T>({
            logCallback(logEvent) {
                if (logEvent === 'INEFFICIENT_LOOKUP') {
                    logWarn('ListStore encountered inefficient lookup in queue');
                }
            },
            defaultSortingDirective,
            sortingDirective,
            groupingDirective,
        });
        if (!this.workspacePanel.queue.pinBackupsToPrimaryUnits) {
            this.syncDirectivesToState();
        }
        makeObservable(this);
    }

    setWorkspacePanel(workspacePanel: WorkspacePanelWithQueue) {
        this.workspacePanel = workspacePanel;
    }

    @action
    public setIsError(isError: boolean): void {
        this.isError = isError;
    }

    @action
    private syncDirectivesToState(): void {
        if (this.workspacePanel.queue.sortByQueueColumnDefinition) {
            const sortingDirective = this.createSortingDirective(
                this.workspacePanel.queue.sortByQueueColumnDefinition,
                this.workspacePanel.queue.sortDirection
            );
            this.listStore.setSortingDirective(sortingDirective);
        }
        if (this.workspacePanel.queue.groupByQueueColumnDefinition) {
            const groupingDirective = this.createGroupingDirective(
                this.workspacePanel.queue.groupByQueueColumnDefinition,
                this.workspacePanel.queue.groupBySortDirection
            );
            this.listStore.setGroupingDirective(groupingDirective);
        }
    }

    @action
    saveFrWorkspaceWindow() {
        this.workspacePanel.queue.delegate.enqueueSaveWorkspaceWindow();
    }

    @action
    setEventQueueFilters(selectedEventQueueFilterOptions: WithColumnDefinition<Option>[]): void {
        // we dont want to delete the existing additional cfs or dispatcher filters because this is handle
        const preservedACfsDispatcherFilters = (
            this.workspacePanel.queue.eventQueueFilters ?? []
        ).filter((filter) => filter.columnDefinition === ColumnDefinitionEnum.ADDITIONAL_CFS.name);

        const newFilters = selectedEventQueueFilterOptions.map(
            (option: any): QueueColumnFilter => ({
                entityId: option.value,
                columnDefinition: option.columnDefinition,
                queueId: this.workspacePanel.queue.id,
            })
        );

        this.workspacePanel.queue.eventQueueFilters = [
            ...preservedACfsDispatcherFilters,
            ...newFilters,
        ];
    }

    @action
    upsertEventQueueFilters(
        eventQueueFilterOptionsToAdd: WithColumnDefinition<Option>[],
        columnDefinition: ColumnDefinitionEnumType
    ): void {
        const newFilters = eventQueueFilterOptionsToAdd.map(
            (option: any): QueueColumnFilter => ({
                entityId: option.value,
                columnDefinition: option.columnDefinition,
                queueId: this.workspacePanel.queue.id,
            })
        );
        const filteredExistingFilters = (this.workspacePanel.queue.eventQueueFilters ?? []).filter(
            (filter) => filter.columnDefinition !== columnDefinition
        );

        this.workspacePanel.queue.eventQueueFilters = [...filteredExistingFilters, ...newFilters];
    }

    @action
    setQueueClasses(newQueueClasses: NonNullable<WorkspacePanelDto['queue']>['queueClasses']) {
        this.workspacePanel.queue.queueClasses = newQueueClasses;
    }

    /**
     * Sets new queue display density
     */
    @action
    setDisplayDensity(density: QueueDisplayDensityEnumType): void {
        this.workspacePanel.queue.displayDensity = density;
    }

    /**
     * check if given rowId exists in listStore (may not match filter directive, but does exist)
     */
    hasRowById(rowId: number): boolean {
        return this.listStore.hasItem(rowId);
    }

    /**
     *
     * check if given rowId row in listStore exists AND matches filter directive
     */
    isRowInList(rowId: number): boolean {
        return this.listStore.getItemIndex(rowId) !== undefined;
    }

    /**
     * Sets initial rows for the queue
     */
    @action
    setRows(rows: T[]): void {
        this.listStore.setItems(rows);
        this.logQueueBreadcrumb('Queue initialized with setRows');
        this.isLoading = false;
    }

    @computed
    get isFiltered(): boolean {
        return !!this.filterQuery;
    }

    protected createFilterDirective(query: string): FilterDirective<T> {
        if (!query) {
            return () => true;
        }
        const lowercaseQuery = query.toLocaleLowerCase();
        return (item: T) => {
            // @ts-expect-error searchValue is not on T.
            // Will follow up on this factoring issue in a separate ticket.
            // https://mark43.atlassian.net/browse/CAD-16474
            return item.searchValue?.indexOf(lowercaseQuery) !== -1;
        };
    }

    /**
     * Sets a new filter query
     */
    @action
    setFilterQuery(query: string): void {
        if (!query) {
            if (!this.manualFilterDirective) {
                this.listStore.setFilterDirective();
            }
            this.filterQuery = '';
            return;
        }
        if (!this.manualFilterDirective) {
            const filterDirective = this.createFilterDirective(query);
            this.listStore.setFilterDirective(filterDirective);
        }
        this.filterQuery = query;
    }

    @action
    clearFilterQuery(): void {
        if (!this.manualFilterDirective) {
            this.listStore.setFilterDirective();
        }
        this.filterQuery = '';
    }

    @action
    switchEventUpdatesNotificationMode(mode: keyof typeof EVENT_UPDATE_NOTIFICATION_MODE): void {
        this.workspacePanel.queue.updateEventUpdatesNotificationMode(mode);
    }

    @action
    toggleShowFullRowUnitTimerUpdates(): void {
        this.workspacePanel.queue.toggleFullRowUnitTimerUpdates();
    }

    protected createSortingDirective(
        columnDefinitionName?: ColumnDefinitionEnumType,
        direction?: WorkspaceQueue['sortDirection']
    ): SortingDirective<T> {
        const comparator: SortingDirective<T>['comparator'] = (
            left: string,
            right: string
        ): ComparatorReturns => {
            const result =
                isNotNullOrUndefined(columnDefinitionName) &&
                columnDefinitionEnum[columnDefinitionName].sortType === 'alphabetical'
                    ? alphabeticalStringComparator(left, right)
                    : alphanumericStringComparator(left, right);
            const isAscending = direction === 'REGULAR_ASCENDING' || !direction;
            return (isAscending ? result : result * -1) as ComparatorReturns;
        };
        if (columnDefinitionName) {
            return {
                getSortValue: columnDefinitionEnum[columnDefinitionName].getSortValue,
                comparator,
            };
        } else {
            // this is a default comparator which sorts by ID
            return {
                getSortValue: (item: RawQueueRowData) => item.id,
                comparator: numberComparator,
            };
        }
    }

    /**
     * Sets a new sort column. If the same column is set the sort will toggle through
     * asc => desc => no sort. persist changes the toggle to asc => desc
     * If no column is set, custom sorting is removed
     */
    @action
    setSort(
        columnDefinitionName?: ColumnDefinitionEnumType,
        sortDirection?: WorkspaceQueue['sortDirection'],
        skipSetSortingDirective?: boolean,
        options?: { skipDefaultSort?: boolean }
    ): void {
        if (
            columnDefinitionName &&
            columnDefinitionName === this.workspacePanel.queue.groupByQueueColumnDefinition
        ) {
            return;
        }
        this.workspacePanel.queue.setSortColumn(columnDefinitionName, options);
        if (sortDirection) {
            this.workspacePanel.queue.setSortDirection(sortDirection);
        }
        if (!skipSetSortingDirective) {
            const sortingDirective = this.createSortingDirective(
                this.workspacePanel.queue.sortByQueueColumnDefinition,
                this.workspacePanel.queue.sortDirection
            );
            if (!this.workspacePanel.queue.sortByQueueColumnDefinition) {
                this.listStore.setSortingDirective();
            } else {
                this.listStore.setSortingDirective(sortingDirective);
            }
        }
        this.logQueueBreadcrumb(
            `setSort updated to sort: ${columnDefinitionName} - ${sortDirection}`
        );
    }

    protected createGroupingDirective(
        columnDefinitionName: ColumnDefinitionEnumType,
        sortDirection: WorkspaceQueue['groupBySortDirection']
    ): GroupingDirective<T> {
        const comparator =
            columnDefinitionEnum[columnDefinitionName].sortType === 'alphabetical'
                ? alphabeticalStringComparator
                : alphanumericStringComparator;
        return {
            getGroupValue: (item: T) => {
                const flatValue = columnDefinitionEnum[columnDefinitionName].getFlatValue(item);
                // It's possible to get strings constituted only of spaces, so we trim just
                // in case that happens to make sure we replace empty strings with a sentinel
                // value down the line.
                return flatValue?.trim() ? flatValue : EMPTY_GROUP_SENTINEL_HEADER;
            },
            comparator: (left: string, right: string) => {
                const result = comparator(left, right);
                return (
                    sortDirection === 'REGULAR_ASCENDING' || !sortDirection ? result : result * -1
                ) as ComparatorReturns;
            },
        };
    }

    /**
     * Sets sort direction of group headers
     */
    @action
    setGroupBySortDirection(
        sortDirection: WorkspaceQueue['groupBySortDirection'],
        skipSetGroupingDirective?: boolean
    ): void {
        this.workspacePanel.queue.setGroupBySortDirection(sortDirection);
        if (this.workspacePanel.queue.groupByQueueColumnDefinition && !skipSetGroupingDirective) {
            const groupingDirective = this.createGroupingDirective(
                this.workspacePanel.queue.groupByQueueColumnDefinition,
                this.workspacePanel.queue.groupBySortDirection
            );
            this.listStore.setGroupingDirective(groupingDirective);
        }
    }

    @action
    setGroupByQueueColumnDefinition(
        columnDefinitionName?: ColumnDefinitionEnumType,
        skipSetGroupingDirective?: boolean
    ): void {
        if (
            columnDefinitionName &&
            columnDefinitionName === this.workspacePanel.queue.sortByQueueColumnDefinition
        ) {
            this.setSort();
        }
        if (
            !this.workspacePanel.queue.groupBySortDirection ||
            columnDefinitionName !== this.workspacePanel.queue.groupByQueueColumnDefinition
        ) {
            this.workspacePanel.queue.setGroupBySortDirection('REGULAR_ASCENDING');
        }
        this.workspacePanel.queue.setGroupByColumn(columnDefinitionName);
        if (!this.workspacePanel.queue.groupByQueueColumnDefinition) {
            this.listStore.setGroupingDirective();
        } else {
            const groupingDirective = this.createGroupingDirective(
                this.workspacePanel.queue.groupByQueueColumnDefinition,
                this.workspacePanel.queue.groupBySortDirection
            );
            if (!skipSetGroupingDirective) {
                this.listStore.setGroupingDirective(groupingDirective);
            }
        }
        this.logQueueBreadcrumb(`setGroupColumn updated to column: ${columnDefinitionName}`);
    }

    /** Selects the row, or clears the selection when `rowId` is null */
    @action
    setSelectedRowId = (rowId: number | null): void => {
        this.listStore.setSelectedItems(rowId === null ? [] : [rowId]);
    };

    @observable
    isSelected = (rowId: number): boolean => {
        return this.listStore.selectedItems[rowId];
    };

    /**
     * Adds or updates queue rows
     */
    @action
    upsertRows(rows: T[]): void {
        // Single pass: upsertItems returns isNew and passesFilter per row,
        // both already computed internally — no redundant pre/post lookups needed.
        const results = this.listStore.upsertItems(rows);

        for (const result of results) {
            const eventQueueRowData = getEventQueueRowData(result.row);

            if (
                result.isNew &&
                result.passesFilter &&
                !eventQueueRowData?.queueFiltersMetadata?.isAssigned
            ) {
                this.playNewEventAlertSound?.(result.row.id, {
                    isAed:
                        eventQueueRowData?.aedState === 'ACCEPTING' ||
                        eventQueueRowData?.aedState === 'ATTEMPTING',
                    callTakerId: eventQueueRowData?.callTakerId,
                    priorityId: eventQueueRowData?.priorityId ?? null,
                    agencyTypeGlobalAttrId: eventQueueRowData?.agencyTypeGlobalAttrId,
                });
            }

            // AED sound: existing behavior unchanged — fires for any row in AED state that passes the filter.
            if (
                result.passesFilter &&
                (eventQueueRowData?.aedState === 'ACCEPTING' ||
                    eventQueueRowData?.aedState === 'ATTEMPTING')
            ) {
                this.playAedSoundForEvent?.(result.row.id, {
                    agencyTypeGlobalAttrId: eventQueueRowData?.agencyTypeGlobalAttrId,
                });
            }
        }
    }

    /**
     * Removes queue rows
     */
    @action
    removeRows(ids: number[]): void {
        this.listStore.removeItems(ids);
        ids.forEach((id) => this.onRemoveRows?.(id));
    }

    /**
     * Logs the state of the queue for monitoring
     * @param message
     */
    private logQueueBreadcrumb(message: string): void {
        logWarn(message, {
            queueId: this.workspacePanel.queue.id,
            entityType: this.workspacePanel.queue.dataEntityType,
            sortByQueueColumnDefinition: this.workspacePanel.queue.sortByQueueColumnDefinition,
            sortDirection: this.workspacePanel.queue.sortDirection,
            groupByQueueColumnDefinition: this.workspacePanel.queue.groupByQueueColumnDefinition,
            groupBySortDirection: this.workspacePanel.queue.groupBySortDirection,
        });
    }
}
